import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { T } from "../tokens";

/** Where the Google sign-in flow starts. Full-page navigation (NOT an XHR) — the
 *  backend 302-redirects the browser to Google. Same path is used everywhere so the
 *  login page and any future landing page share one OAuth entrypoint. */
export const GOOGLE_OAUTH_START = "/api/auth/google";

/** Fetch which auth options the backend can actually complete. Returns only booleans;
 *  no client id/secret ever crosses the wire (see GET /auth/config). */
export function useGoogleEnabled(): boolean {
  const { data } = useQuery({
    queryKey: ["auth-config"],
    queryFn: async () => {
      const { data } = await axios.get("/api/auth/config");
      return data as { googleEnabled: boolean };
    },
    staleTime: 5 * 60_000,
    // Defense in depth (hardening/auth-session): a transient failure here
    // must never be read as "Google is disabled" — the button hiding is
    // supposed to mean the backend has no Google client configured, not
    // "the network had a bad second." The root cause (this endpoint getting
    // caught by the global rate limiter during a burst of stale-token 401s)
    // is fixed server-side by exempting GET /auth/config from rate limiting;
    // this retry is a second line of defense against any other transient
    // failure of the same request.
    retry: 3,
  });
  return data?.googleEnabled === true;
}

/**
 * "Continue with Google" button. Renders NOTHING when Google OAuth is not configured
 * on the backend (`googleEnabled=false`) — so a dead button is never shown. Clicking it
 * does a full-page navigation to the backend, which redirects to Google; the return trip
 * is handled by the existing /oauth-callback route.
 */
export function GoogleOAuthButton() {
  const enabled = useGoogleEnabled();
  if (!enabled) return null;

  return (
    <button
      type="button"
      onClick={() => { window.location.href = GOOGLE_OAUTH_START; }}
      style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "center",
        gap: "8px", padding: "10px 12px", cursor: "pointer",
        background: T.s2, border: `1px solid ${T.b1}`, borderRadius: "7px",
        color: T.t0, fontFamily: T.body, fontSize: "13px", fontWeight: 500,
      }}
      aria-label="Continue with Google"
    >
      <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.6l-6.6-5.6C29.6 34.6 26.9 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.6 5.6C41.9 35.6 44 30.2 44 24c0-1.3-.1-2.3-.4-3.5z"/>
      </svg>
      Continue with Google
    </button>
  );
}
